import { Box, Button, IconButton, MenuItem, styled, TextField } from "@mui/material";
import { getTitle } from "utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { snack } from "components/toast";
import { useNavigate } from "react-router-dom";


interface TextFieldProps {
  value: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  disabled?: boolean;
  type?: string;
  withCopy?: boolean;
  inputProps?: any;
  navigateTo?: string;
}

export function CustomTextField(props: TextFieldProps) {
  const { value, onChange, inputProps, name, disabled = false, type = "text", withCopy, navigateTo } = props;
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (!disabled && navigateTo) {
      e.stopPropagation();  // Prevent text field event bubbling
      navigate(navigateTo);  // Navigate to the desired route
    }
  };

  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.navigator.clipboard.writeText(value).then(
      () => snack.success("Copied"),
      () => snack.error("Something went wrong")
    );
  };

  return (
    <Box
      onClick={navigateTo && !disabled ? handleClick : undefined}
      sx={{
        cursor: !disabled && navigateTo ? 'pointer' : 'default',
      }}
    >
      <TextField
        InputProps={{
          ...withCopy && {
            endAdornment: (
              <IconButton onClick={onCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            ),
          }
        }}
        sx={{
          textDecoration: navigateTo ? 'underline' : '',
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "& .MuiOutlinedInput-input.Mui-disabled": {
            WebkitTextFillColor: "black",  // Ensure disabled text is black
          },
          "& .MuiOutlinedInput-root fieldset": {
            border: 0,  // Remove border
          },
          // Ensure pointer-events are disabled for input field itself if navigateTo is set
          pointerEvents: navigateTo ? 'none' : 'auto',
          cursor: !disabled && navigateTo ? 'pointer' : 'default',  // Ensure pointer cursor on TextField itself
        }}
        name={name}
        type={type}
        fullWidth
        value={value}
        onChange={onChange}
        disabled={disabled}
        inputProps={{
          ...inputProps,
          readOnly: disabled, // Ensure the TextField appears as read-only when disabled
        }}
      />
    </Box>
  );
}


interface SelectProps {
  value: string | boolean;
  name?: string;
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  options: { label: string; value: any }[];
  disabled?: boolean;
}

export function CustomSelect({ value, options, onChange, name, disabled = false }: SelectProps) {
  return (
    <StyledTextField
      select
      name={name}
      fullWidth
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {options.map((option, index) => (
        <MenuItem value={option.value} key={index}>
          {getTitle(option.label)}
        </MenuItem>
      ))}
    </StyledTextField>
  );
}

export const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderWidth: 0,
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1,
    },
    "&.Mui-disabled": {
      WebkitTextFillColor: "black",
    },
  },
}));
